import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import YellowCardSvg from "../assets/event_icons/Yellow Card.svg";
import RedCardSvg from "../assets/event_icons/Red Card.svg";
import Save from "../assets/event_icons/Save.svg";
import Shot from "../assets/event_icons/Shot.svg";
import Goal from "../assets/event_icons/Goal.svg";
import Penalty from "../assets/event_icons/Saved penatly.svg";
import { IInjuryEvents, IMatchLevelEvents } from "../types/ITimeline";
import { drawLine } from "../utils/drawPitchfunctions";
import { useTheme } from "../contexts/ThemeContext";
import { ToolTipStyles } from "../styled-components/Tooltip.Styles";
import LabelText from "../ui/LabelText";
import ReactDOMServer from "react-dom/server";
import EventPlayerCard from "./EventPlayerCard";
import IApiResponse from "../types/IApiResponse";
import { postData } from "../services/api";
import apiUrls from "../config/apiUrls";
import handleApiResponse from "../utils/handleApiResponse";
import { IPlayerCardEventDetails } from "../types/IPlayerCardEventDetails";
import { useLoading } from "../contexts/LoadingContext";

interface IEventTimeLineProps {
  matchId: number;
  injuryEvents: IInjuryEvents;
  matchLevelEvents: IMatchLevelEvents[];
  matchEndTime: number;
  direction?: string;
}

const EventTimeLine: React.FC<IEventTimeLineProps> = ({
  matchId,
  matchLevelEvents = [],
  matchEndTime,
  injuryEvents,
  direction = "previous",
}) => {
  const { theme } = useTheme();
  const { startLoading, stopLoading } = useLoading();
  const mode = window.localStorage.getItem("theme");
  const svgRef = useRef<SVGSVGElement>(null);
  const [eventsData, setEventsData] = useState<IMatchLevelEvents[]>([]);
  const [playerCardDetails, setPlayerCardDeatils] = useState<IPlayerCardEventDetails | null>();
  const [playerCardID, setPlayerCardID] = useState<number>(0);
  const [eventType, setEventType] = useState<string>();
  const [isPlayerCardOpen, setIsPlayerCardOpen] = useState(false);
  const [playerCardPosition, setPlayerCardPosition] = useState<{ x: number; y: number } | null>(null); // Track player card position
  const tooltipRef = useRef<HTMLDivElement>(null);

  function getColor(event: IMatchLevelEvents, hover: boolean = false): string {
    if (["goal", "yellow_card", "red_card", "own_goal"].includes(event.eventType)) {
      return event.homeAway === "home" ? "#00A1E4" : "#FF3864";
    } else {
      return hover ? (event.homeAway === "home" ? "#00A1E4" : "#FF3864") : theme.playerColor; // White when not hovering, based on homeAway when hovering
    }
  }

  const fetchPlayerData = async (eventId: string, eventType: string) => {
    try {
      startLoading();
      const response: IApiResponse<IPlayerCardEventDetails> = await postData(`${apiUrls.GetPlayerCardEventDetails}`, {
        matchId: matchId,
        eventId: eventId,
        eventType: eventType,
      });
      handleApiResponse(
        response,
        (playerCardData) => {
          stopLoading();
          setPlayerCardDeatils(playerCardData);
          setIsPlayerCardOpen(true);
          return playerCardData;
        },
        (error) => {
          stopLoading();
          console.error("Error fetching player Data:", error);
        }
      );
    } catch (error) {
      stopLoading();
      console.error("Error fetching player data:", error);
      return null;
    }
  };

  useEffect(() => {
    setEventsData(matchLevelEvents || []);
  }, [matchLevelEvents]);

  useEffect(() => {
    const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3.select<SVGSVGElement, unknown>(svgRef.current!);
    const createEventTimeline = () => {
      const container = document.getElementById("timeline-container");
      if (!container) return;
      const timelineWidth: number = container.clientWidth;
      const height: number = 100;
      const totalTimeInSeconds = matchEndTime!;
      // Create a scale for time
      const xScale: d3.ScaleLinear<number, number> = d3
        .scaleLinear()
        .domain([0, totalTimeInSeconds]) // Domain in seconds
        .range([0, timelineWidth]);
      // Clear previous elements to avoid duplication
      svg.selectAll("*").remove();
      // Create a group element
      const group = svg.append("g");
      // Render solid line at the start
      drawLine(group, -1, 40, -1, 60, theme.playerCardBorderColor, 5);
      // Draw lower line
      // @ts-ignore
      drawLine(svg, 0, height / 2 + 40, timelineWidth, height / 2 + 40, theme.playerCardBorderColor, 3);
      matchLevelEvents.length > 0 &&
        svg
          .append("line")
          .attr("y1", height / 2 + 40)
          .attr("y2", height / 2 + 40)
          .style("stroke-width", 3)
          .style("stroke", "#18b686")
          .attr(direction === "next" ? "x1" : "x2", direction === "next" ? 0 : timelineWidth)
          .attr(direction === "next" ? "x2" : "x1", direction === "next" ? timelineWidth : 0)
          .transition()
          .duration(500)
          .ease(d3.easeCubicOut)
          .attr(direction === "next" ? "x2" : "x1", direction === "next" ? -50 : timelineWidth + 50)
          .transition()
          .duration(500)
          .ease(d3.easeCubicOut)
          .attr(direction === "next" ? "x2" : "x1", direction === "next" ? timelineWidth : 0);
      // Render lines for injury events
      if (injuryEvents) {
        drawLine(group, xScale(0), height / 2, xScale(injuryEvents.injuryFirstHalf.start), height / 2, theme.playerCardBorderColor, 2);
        drawLine(
          group,
          xScale(injuryEvents.injuryFirstHalf.start),
          40,
          xScale(injuryEvents.injuryFirstHalf.start),
          60,
          theme.playerCardBorderColor,
          2
        );
        drawLine(
          group,
          xScale(injuryEvents.injuryFirstHalf.start),
          height / 2,
          xScale(injuryEvents.injuryFirstHalf.end),
          height / 2,
          theme.playerCardBorderColor,
          2,
          undefined,
          1
        ).style("stroke-dasharray", "4");
        drawLine(
          group,
          xScale(injuryEvents.injuryFirstHalf.end),
          40,
          xScale(injuryEvents.injuryFirstHalf.end),
          60,
          theme.playerCardBorderColor,
          2
        );
        drawLine(
          group,
          xScale(injuryEvents.injuryFirstHalf.end),
          height / 2,
          xScale(injuryEvents.injurySecondHalf.start),
          height / 2,
          theme.playerCardBorderColor,
          2
        );
        drawLine(
          group,
          xScale(injuryEvents.injurySecondHalf.start),
          40,
          xScale(injuryEvents.injurySecondHalf.start),
          60,
          theme.playerCardBorderColor,
          2
        );
        drawLine(
          group,
          xScale(injuryEvents.injurySecondHalf.start),
          height / 2,
          xScale(injuryEvents.injurySecondHalf.end),
          height / 2,
          theme.playerCardBorderColor,
          2,
          undefined,
          1
        ).style("stroke-dasharray", "4");
        drawLine(
          group,
          xScale(injuryEvents.injurySecondHalf.end),
          40,
          xScale(injuryEvents.injurySecondHalf.end),
          60,
          theme.playerCardBorderColor,
          2
        );
      };
      svg
        .selectAll("circle")
        .data(eventsData) // No unique key needed
        .enter()
        .append("circle")
        // .attr("cx", (d) => xScale(d.matchTimestampSeconds)) //+ jitter(10))
        .attr("cy", height / 2)
        .attr("r", 4)
        .style("fill", (d) => getColor(d))
        .attr("cx", (d) => xScale(d.matchTimestampSeconds))
        .transition()
        .duration(500) // Only animate if matchData.length > 0
        .ease(d3.easeCubicOut)
        .attr("cx", (_, i) => (direction === "next" ? (eventsData.length - i) * -50 : timelineWidth + 80 + i * 50))
        .transition()
        .duration(500) // Only animate if matchData.length > 0
        .ease(d3.easeCubicOut)
        .attr("cx", (d) => xScale(d.matchTimestampSeconds));
      const tooltip = d3.select(tooltipRef.current!);
      const generateTooltipContent = (eventType: string, playerName: string, timestamp: number) => {
        return ReactDOMServer.renderToStaticMarkup(
          <div>
            <LabelText className="event-tooltip-title">{eventType}</LabelText> •
            <LabelText>
              {" "}
              {playerName} {(timestamp / 60).toFixed(2)}
            </LabelText>
          </div>
        );
      };
      // Render event icons
      const dataIcons = svg
        .selectAll("image")
        .data(eventsData) // Use unique key for data binding
        .enter()
        .append("image")
        .attr("class", (d) => (["goal", "yellow_card", "red_card", "own_goal"].includes(d.eventType) ? "initialIcons" : "laterIcons"))
        .attr("y", height / 2 - 25)
        .style("height", "16px")
        .style("width", "16px")
        .attr("xlink:href", (d) => {
          switch (d.eventType) {
            case "goal":
              return Goal;
            case "own_goal":
              return Goal;
            case "yellow_card":
              return YellowCardSvg;
            case "red_card":
              return RedCardSvg;
            case "save":
              return Save;
            case "shot":
              return Shot;
            case "free_kick_shot":
              return Shot;
            case "penalty_save":
              return Penalty;
            default:
              return null;
          }
        })
        .style("opacity", (d) => {
          return ["goal", "yellow_card", "red_card", "own_goal"].includes(d.eventType) ? "1" : "0";
        })
        .style("filter", (d) => {
          return ["goal", "yellow_card", "red_card", "own_goal"].includes(d.eventType)
            ? "none"
            : mode === "light"
            ? "grayscale(100%) brightness(-100%)"
            : "none";
        })
        .on("click", (event, d) => {
          const [x, y] = d3.pointer(event);
          adjustPositionForPlayerCard(x, y);
          setEventType(d.eventType);
          setPlayerCardID(d.playerId); // Assuming d contains player data
          const playerCardData = fetchPlayerData(d.eventId, d.eventType);
        })
        .on("mouseover", (event, d) => {
          const [x, y] = d3.pointer(event);
          tooltip
            .style("visibility", "visible")
            .style("top", `${y - 50}px`)
            .style("left", `${x + 10}px`)
            .html(generateTooltipContent(d.eventType, d.playerName, d.matchTimestampSeconds));
        })
        .on("mousemove", (event) => {
          const [x, y] = d3.pointer(event);
          tooltip.style("top", `${y - 50}px`).style("left", `${x + 10}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
        })
        .attr("x", (d) => xScale(d.matchTimestampSeconds) - 8)
        .transition()
        .duration(500) // Only animate if matchData.length > 0
        .ease(d3.easeCubicOut)
        .attr("x", (_, i) => (direction === "next" ? (eventsData.length - i) * -50 : timelineWidth + 80 + i * 50))
        .transition()
        .duration(500) // Only animate if matchData.length > 0
        .ease(d3.easeCubicOut)
        .attr("x", (d) => xScale(d.matchTimestampSeconds) - 8);
      svg.on("mousemove", function (event) {
        const [x] = d3.pointer(event);
        const hoverTime = xScale.invert(x);
        svg.selectAll<SVGCircleElement, IMatchLevelEvents>("circle").style("fill", (d) => getColor(d, true));
        d3.selectAll(".laterIcons").style("opacity", 1);
      });
      svg.on("mouseleave", function () {
        svg.selectAll<SVGCircleElement, IMatchLevelEvents>("circle").style("fill", (d) => getColor(d, false));
        d3.selectAll(".laterIcons").style("opacity", 0);
      });
      drawLine(group, timelineWidth, 40, timelineWidth, 60, "#FFFFFF80", 2);
    };

    const adjustPositionForPlayerCard = (x: number, y: number) => {
      const container = document.getElementById("timeline-container");
      if (!container) return;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const cardWidth = 300; // Approximate width of the player card
      const cardHeight = 200; // Approximate height of the player card
      let adjustedX = x;
      if (x + cardWidth > containerWidth) {
        adjustedX = containerWidth - cardWidth;
      } else if (x < 0) {
        adjustedX = 0;
      }
      const adjustedY = y + cardHeight > containerHeight ? y - cardHeight : y;
      setPlayerCardPosition({ x: adjustedX, y: y });
    };
    createEventTimeline();
    window.addEventListener("resize", createEventTimeline);
    window.addEventListener("orientationchange", createEventTimeline);
    return () => {
      window.removeEventListener("resize", createEventTimeline);
      window.removeEventListener("orientationchange", createEventTimeline);
    };
  }, [eventsData, injuryEvents, matchEndTime, mode, theme]);
  
  const handleCardClose = () => {
    setPlayerCardID(0);
    setPlayerCardDeatils(null);
    setIsPlayerCardOpen(false);
  };

  return (
    <div id="timeline-container" className="timeline-container" style={{ width: "100%" }}>
      <svg ref={svgRef} width="100.50%" height={100} />
      <ToolTipStyles ref={tooltipRef} className="tooltip-wrapper tooltip" />
      {isPlayerCardOpen && playerCardPosition && (
        <div
          style={{
            position: "absolute",
            left: `${playerCardPosition.x}px`,
            top: `${playerCardPosition.y + 20}px`,
            zIndex: 1000,
          }}
        >
          <EventPlayerCard playerCardDetails={playerCardDetails} onClose={handleCardClose} eventType={eventType} />
        </div>
      )}
    </div>
  );
};

export default EventTimeLine;
